import styled from '@emotion/styled';

import { COLORS3, FONT, MEDIA_QUERY } from '../styles/emotion';

export type HeadingColor = 'BLACK_100' | 'BLACK_075' | 'BLACK_050' | 'WHITE_100';
export type HeadingElement = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';

const LETTER_SPACING_NEG_4 = '-0.04em';
const LETTER_SPACING_NEG_3 = '-0.03em';
const CUSTOM_MOBILE_H1_SIZE = '42px';

const getFontWeight = (strong?: boolean, serif?: boolean): string => {
  // Our serif font (New Spirit) is only loaded at 500 font weight for performance reasons
  if (serif) {
    return FONT.WEIGHT.MEDIUM;
  }
  return strong ? FONT.WEIGHT.SEMI_BOLD : FONT.WEIGHT.REGULAR;
};

export const mapPresentationToStyles = (presentation: HeadingElement): string => {
  switch (presentation) {
    case 'h6':
      return FONT.textSizeUltraCompact(FONT.SIZE.SMALL);
    case 'h5':
      return `
        ${FONT.textSizeUltraCompact('18px')}

        ${MEDIA_QUERY.EXCLUDE_MOBILE} {
          ${FONT.textSizeUltraCompact(FONT.SIZE.REGULAR)}
        }
      `;
    case 'h4':
      return `
        ${FONT.textSizeCompact(FONT.SIZE.REGULAR)}
        letter-spacing: ${LETTER_SPACING_NEG_3};

        ${MEDIA_QUERY.EXCLUDE_MOBILE} {
          ${FONT.textSizeCompact(FONT.SIZE.LARGE)}
        }
      `;
    case 'h3':
      return `
        ${FONT.textSizeCompact(FONT.SIZE.LARGE)}
        letter-spacing: ${LETTER_SPACING_NEG_4};

        ${MEDIA_QUERY.EXCLUDE_MOBILE} {
          ${FONT.textSizeCompact(FONT.SIZE.LARGER)}
        }
      `;
    case 'h2':
      return `
        font-size: ${FONT.SIZE.LARGER};
        line-height: ${FONT.SIZE.LARGER};
        letter-spacing: ${LETTER_SPACING_NEG_4};

        ${MEDIA_QUERY.EXCLUDE_MOBILE} {
          font-size: ${FONT.SIZE.SEMI_HUGE};
          line-height: ${FONT.SIZE.SEMI_HUGE};
        }
      `;
    case 'h1':
    default:
      return `
        font-size: ${CUSTOM_MOBILE_H1_SIZE};
        line-height: ${CUSTOM_MOBILE_H1_SIZE};
        letter-spacing: ${LETTER_SPACING_NEG_4};

        ${MEDIA_QUERY.EXCLUDE_MOBILE} {
          font-size: ${FONT.SIZE.EXTRA_HUGE};
          line-height: ${FONT.SIZE.EXTRA_HUGE};
        }
      `;
  }
};

export const Heading = styled.h1<{
  presentation: HeadingElement;
  color?: HeadingColor;
  strong?: boolean;
  serif?: boolean;
}>`
  margin: 0;
  padding: 0;
  letter-spacing: normal;
  ${({ color }) => `color: ${COLORS3[color as keyof typeof COLORS3]};`}
  font-weight: ${({ strong, serif }) => getFontWeight(strong, serif)};
  font-family: ${({ serif }) => (serif ? FONT.FAMILY.SERIF : FONT.FAMILY.SANS_SERIF)};
  ${({ presentation }) => mapPresentationToStyles(presentation)}
`;
